import { useState } from 'react'
import { LoaderFunctionArgs, ShouldRevalidateFunction, useLoaderData, useLocation, useRevalidator, useSearchParams } from 'react-router-dom'

import { Settings, fetchSettings, updateHealthCenter, updateProfessional } from '../../api/settings'
import { Button, ErrorList, Modal, Panel, PanelBody, useToast } from '../../components/common'
import { ApiError } from '../../lib/api'
import { CalendarSettingsForm } from './CalendarSettingsForm'
import { PlanPanel } from './PlanPanel'
import { PrescriptionForm } from './PrescriptionForm'
import { HealthCenterForm, PasswordForm, ProfileForm } from './forms'
import { useUnsavedChanges } from './useUnsavedChanges'
import styles from './SettingsPage.module.scss'

type TabKey = 'professional' | 'calendar' | 'health-center' | 'prescriptions'

const TAB_LABELS: Readonly<Record<TabKey, string>> = {
  professional: 'Personal',
  calendar: 'Agenda',
  'health-center': 'Centro',
  prescriptions: 'Prescripción',
}

const TAB_ORDER: readonly TabKey[] = ['professional', 'calendar', 'health-center', 'prescriptions']

function _getVisibleTabs(settings: Settings): readonly TabKey[] {
  return TAB_ORDER.filter((tab) => {
    if (tab === 'health-center') {
      return settings.permissions.administrator
    }
    if (tab === 'prescriptions') {
      return (settings.profession?.can_prescript ?? false) && settings.permissions.administrator
    }
    if (tab === 'calendar') {
      return settings.permissions.calendar
    }
    return true
  })
}

function _getErrors(requestError: unknown): readonly string[] {
  const body = requestError instanceof ApiError ? (requestError.body as { errors?: string[] } | null) : null
  return body?.errors ?? ['Ocurrió un error, inténtalo nuevamente']
}

export function loader({ params }: LoaderFunctionArgs): Promise<Settings> {
  return fetchSettings(Number(params.healthCenterId))
}

export const shouldRevalidate: ShouldRevalidateFunction = ({ currentUrl, nextUrl, defaultShouldRevalidate }) => {
  const isTabChange = currentUrl.pathname === nextUrl.pathname && currentUrl.href !== nextUrl.href
  return isTabChange ? false : defaultShouldRevalidate
}

export default function SettingsPage() {
  const showToast = useToast()
  const { revalidate } = useRevalidator()
  const settings = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const [searchParams, setSearchParams] = useSearchParams()
  const { hash } = useLocation()
  const [errors, setErrors] = useState<readonly string[]>([])
  const unsaved = useUnsavedChanges()

  const tabs = _getVisibleTabs(settings)
  // Older links and server redirects still name the tab by hash, e.g. `edit#calendar`.
  const requested = (searchParams.get('tab') ?? (hash.slice(1) || null)) as TabKey | null
  const active: TabKey = requested && tabs.includes(requested) ? requested : (tabs[0] ?? 'professional')

  const goToTab = (tab: TabKey) =>
    unsaved.guard(() => {
      setSearchParams({ tab }, { replace: true })
    })

  const submit = async (send: () => Promise<{ notice: string }>) => {
    setErrors([])
    try {
      const result = await send()
      showToast('success', result.notice)
      revalidate()
    } catch (requestError) {
      setErrors(_getErrors(requestError))
      throw requestError
    }
  }

  const saveProfessional = (values: Record<string, string>) =>
    submit(() => updateProfessional(settings.paths.professional, values))

  const saveHealthCenter = (section: string) => (values: Record<string, string | number>) =>
    submit(() => updateHealthCenter(settings.paths.health_center, values, `#${section}`))

  const formProps = { settings, onDirty: unsaved.markDirty, onClean: unsaved.markClean }

  return (
    <>
      <ErrorList messages={errors} />
      <div className="col-xs-12 col-lg-offset-1">
        <h2 id="sizes">
          <div className="blue-subtitle">Vista general de la cuenta</div>
        </h2>
      </div>

      <div className={`row ${styles.page}`}>
        <div className="col-md-6 col-xs-12 col-md-offset-1">
          <div className="row">
            <Panel className={styles.sidePanel}>
              <div className={`well ${styles.well}`}>
                <PanelBody>
                  <div className="row">
                    <div id="menu-list" className="list-group" role="tablist">
                      {tabs.map((tab) => (
                        <div key={tab} className={`col-xs-6 col-md-${Math.floor(12 / tabs.length)}`}>
                          <a
                            className={`list-group-item list-group-item-action${tab === active ? ' active' : ''}`}
                            href={`#${tab}`}
                            role="tab"
                            onClick={(event) => {
                              event.preventDefault()
                              goToTab(tab)
                            }}
                          >
                            {TAB_LABELS[tab]}
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="col-xs-12">
                    <div className="tab-content">
                      {active === 'professional' && (
                        <div id="professional" className="tab-pane active" role="tabpanel">
                          <ProfileForm {...formProps} onSubmit={saveProfessional} />
                        </div>
                      )}
                      {active === 'health-center' && (
                        <div id="health-center" className="tab-pane active" role="tabpanel">
                          <div className="col-xs-12">
                            <HealthCenterForm {...formProps} onSubmit={saveHealthCenter('health-center')} />
                          </div>
                        </div>
                      )}
                      {active === 'prescriptions' && (
                        <div id="prescriptions" className="tab-pane active" role="tabpanel">
                          <div className="col-xs-12">
                            <PrescriptionForm
                              {...formProps}
                              onSubmit={saveHealthCenter('prescriptions')}
                              onEditSection={(section) => goToTab(section as TabKey)}
                            />
                          </div>
                        </div>
                      )}
                      {active === 'calendar' && (
                        <div id="calendar" className="tab-pane active" role="tabpanel">
                          <div className="col-xs-12">
                            <CalendarSettingsForm {...formProps} onSubmit={saveHealthCenter('calendar')} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </PanelBody>
              </div>
            </Panel>
          </div>
        </div>

        <div className="col-md-4 col-xs-12">
          <div className="col-xs-12">
            <PlanPanel settings={settings} onNavigate={unsaved.guard} />
          </div>
          <div className="col-xs-12">
            <Panel className={styles.sidePanel}>
              <div className={`well ${styles.well}`}>
                <div className="row">
                  <PasswordForm onSubmit={saveProfessional} />
                </div>
              </div>
            </Panel>
          </div>
        </div>
      </div>

      <Modal
        open={unsaved.pending !== null}
        onClose={unsaved.dismiss}
        id="changes-not-saved-modal"
        dialogClassName={styles.unsavedDialog}
        header={<h5 className={`modal-title ${styles.centered}`}>Tienes cambios que no han sido guardados</h5>}
        footer={
          <>
            <div className="col-xs-2 col-xs-offset-4">
              <Button id="continue" className={styles.answer} onClick={() => void unsaved.saveThenContinue()}>
                Si
              </Button>
            </div>
            <div className="col-xs-2">
              <Button id="cancel" variant="secondary" className={styles.answer} onClick={unsaved.discardThenContinue}>
                No
              </Button>
            </div>
          </>
        }
      >
        <p className={styles.centered}>¿Deseas guardar tus cambios antes de continuar?</p>
      </Modal>
    </>
  )
}
